import React, { useState, useCallback, useEffect } from 'react';
import PharmacyHomePage from './PharmacyHomePage';
import PharmacyCatalogPage from './PharmacyCatalogPage';
import PharmacyCartPage from './PharmacyCartPage';
import PharmacyContactPage from './PharmacyContactPage';
import PartenariatsPage from './PartenariatsPage';
import RecrutementPage from './RecrutementPage';
import JobDetailPage from './JobDetailPage';
import PharmacyPathologyPage from './PharmacyPathologyPage';
import PharmacyExpertisesPage from './PharmacyExpertisesPage';
import PharmacyGardePage from './PharmacyGardePage';
import PharmacyLocalSeoPage from './PharmacyLocalSeoPage';
import PharmacyAreaSeoPage from './PharmacyAreaSeoPage';
import { LOCAL_AREA_SLUGS } from './localAreasData';
import AboutPage from './AboutPage';
import BlogListPage from './BlogListPage';
import BlogArticlePage from './BlogArticlePage';
import PharmacyChatbot from './PharmacyChatbot';
import TeaserPage from '../TeaserPage';

// URL <-> page mapping so that Google can crawl SEO-critical pages via their
// human-readable URL even though internal routing is state-based.
// Any URL not in this map falls back to `home`.
const URL_TO_PAGE = {
  '/': 'home',
  '/pharmacie-de-garde-abidjan': 'garde',
  '/pharmacie-cocody-angre': 'pharmacie-cocody-abidjan',
  '/a-propos': 'about',
  '/partenariats': 'partenariats',
  '/recrutement': 'recrutement',
  '/contact': 'contact',
  '/catalogue': 'catalog',
  '/panier': 'cart',
  '/pathologies': 'pathology',
  '/expertises': 'expertises',
  '/blog': 'blog',
  '/decouvrir/skincare': 'teaser:skincare',
  '/decouvrir/health': 'teaser:health',
  '/decouvrir/bebe': 'teaser:bebe',
  // Local SEO area landing pages (generic component, slug-driven)
  ...Object.fromEntries(LOCAL_AREA_SLUGS.map((s) => [`/${s}`, `area:${s}`])),
};
const PAGE_TO_URL = Object.fromEntries(
  Object.entries(URL_TO_PAGE).map(([url, page]) => [page, url])
);

const PharmacyApp = ({ onStaffLogin }) => {
  // Initial page bootstrap handles `/blog/<slug>` too
  const [currentPage, setCurrentPage] = useState(() => {
    if (typeof window === 'undefined') return 'home';
    const p = window.location.pathname;
    if (p.startsWith('/blog/') && p !== '/blog/') return 'blog-article';
    if (p.startsWith('/recrutement/') && p !== '/recrutement/') return 'recrutement-detail';
    return URL_TO_PAGE[p] || 'home';
  });
  const [pageParams, setPageParams] = useState(() => {
    if (typeof window === 'undefined') return {};
    const p = window.location.pathname;
    if (p.startsWith('/blog/') && p !== '/blog/') {
      return { slug: decodeURIComponent(p.replace('/blog/', '').replace(/\/$/, '')) };
    }
    if (p.startsWith('/recrutement/') && p !== '/recrutement/') {
      return { slug: decodeURIComponent(p.replace('/recrutement/', '').replace(/\/$/, '')) };
    }
    return {};
  });
  const [cart, setCart] = useState([]);

  // Sync with browser back/forward buttons
  useEffect(() => {
    const onPop = () => {
      const p = window.location.pathname;
      if (p.startsWith('/blog/') && p !== '/blog/') {
        setCurrentPage('blog-article');
        setPageParams({ slug: decodeURIComponent(p.replace('/blog/', '').replace(/\/$/, '')) });
      } else if (p.startsWith('/recrutement/') && p !== '/recrutement/') {
        setCurrentPage('recrutement-detail');
        setPageParams({ slug: decodeURIComponent(p.replace('/recrutement/', '').replace(/\/$/, '')) });
      } else {
        setCurrentPage(URL_TO_PAGE[p] || 'home');
        setPageParams({});
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((page, params = {}) => {
    if (page === 'staff-login') {
      onStaffLogin();
      return;
    }
    // Recrutement detail via shortcut string "recrutement:<slug>"
    if (typeof page === 'string' && page.startsWith('recrutement:')) {
      const slug = page.slice('recrutement:'.length);
      setCurrentPage('recrutement-detail');
      setPageParams({ slug });
      const targetUrl = `/recrutement/${slug}`;
      if (typeof window !== 'undefined' && window.location.pathname !== targetUrl) {
        window.history.pushState({ page: 'recrutement-detail', params: { slug } }, '', targetUrl);
      }
      window.scrollTo(0, 0);
      return;
    }
    // Teaser pages: 'teaser' + {kind} → internal id `teaser:<kind>` with URL `/decouvrir/<kind>`
    if (page === 'teaser' && params.kind) {
      const internal = `teaser:${params.kind}`;
      setCurrentPage(internal);
      setPageParams({});
      const targetUrl = `/decouvrir/${params.kind}`;
      if (typeof window !== 'undefined' && window.location.pathname !== targetUrl) {
        window.history.pushState({ page: internal, params: {} }, '', targetUrl);
      }
      window.scrollTo(0, 0);
      return;
    }
    setCurrentPage(page);
    setPageParams(params);
    // URL handling
    let targetUrl = PAGE_TO_URL[page];
    if (page === 'blog-article' && (params.slug || params.fallbackId)) {
      targetUrl = `/blog/${params.slug || params.fallbackId}`;
    }
    if (page === 'recrutement-detail' && params.slug) {
      targetUrl = `/recrutement/${params.slug}`;
    }
    if (targetUrl && typeof window !== 'undefined' && window.location.pathname !== targetUrl) {
      window.history.pushState({ page, params }, '', targetUrl);
    }
    window.scrollTo(0, 0);
  }, [onStaffLogin]);

  const addToCart = useCallback((product) => {
    setCart(prevCart => {
      const existing = prevCart.find(item => item.id === product.id);
      if (existing) {
        return prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { ...product, quantity: 1 }];
    });
  }, []);

  const updateCart = useCallback((newCart) => {
    setCart(newCart);
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const renderPage = () => {
    // Dynamic area SEO pages (pharmacie-riviera-golf, pharmacie-2-plateaux, etc.)
    if (typeof currentPage === 'string' && currentPage.startsWith('area:')) {
      return (
        <PharmacyAreaSeoPage
          areaSlug={currentPage.slice(5)}
          onBack={() => navigate('home')}
        />
      );
    }
    // Teaser pages (skincare / health / bebe) — public, sans login
    if (typeof currentPage === 'string' && currentPage.startsWith('teaser:')) {
      const kind = currentPage.slice(7);
      return (
        <TeaserPage
          kind={kind}
          onBack={() => navigate('home')}
        />
      );
    }
    switch (currentPage) {
      case 'home':
        return (
          <PharmacyHomePage
            onNavigate={navigate}
            cartCount={cartCount}
          />
        );
      
      case 'catalog':
        return (
          <PharmacyCatalogPage
            onNavigate={navigate}
            initialSearch={pageParams.search || ''}
            initialCategory={pageParams.category || ''}
            cart={cart}
            onAddToCart={addToCart}
          />
        );
      
      case 'cart':
        return (
          <PharmacyCartPage
            onNavigate={navigate}
            cart={cart}
            onUpdateCart={updateCart}
            onClearCart={clearCart}
          />
        );
      
      case 'contact':
        return (
          <PharmacyContactPage
            onNavigate={navigate}
          />
        );

      case 'partenariats':
        return (
          <PartenariatsPage
            onBack={() => navigate('home')}
            onNavigate={navigate}
          />
        );

      case 'recrutement':
        return (
          <RecrutementPage
            onBack={() => navigate('home')}
            onNavigate={navigate}
          />
        );

      case 'recrutement-detail':
        return (
          <JobDetailPage
            slug={pageParams.slug}
            onBack={() => navigate('recrutement')}
            onApply={(slug, postType) => {
              navigate('recrutement', {});
              // Scroll & prefill happens next tick after navigation
              setTimeout(() => {
                const el = document.getElementById('formulaire');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
                // Broadcast a simple event so RecrutementPage can prefill
                window.dispatchEvent(
                  new CustomEvent('sada:prefill-job', { detail: { slug, postType } }),
                );
              }, 100);
            }}
          />
        );
      
      case 'pathology':
        return (
          <PharmacyPathologyPage
            pathologyId={pageParams.id}
            onBack={() => navigate('expertises')}
            onNavigate={navigate}
          />
        );
      
      case 'expertises':
        return (
          <PharmacyExpertisesPage
            onBack={() => navigate('home')}
            onNavigate={navigate}
          />
        );
      
      case 'garde':
        return (
          <PharmacyGardePage
            onBack={() => navigate('home')}
            onNavigate={navigate}
          />
        );
      
      case 'pharmacie-cocody-abidjan':
        return (
          <PharmacyLocalSeoPage
            onBack={() => navigate('home')}
            onNavigate={navigate}
          />
        );

      case 'about':
        return (
          <AboutPage onBack={() => navigate('home')} />
        );

      case 'blog':
        return (
          <BlogListPage
            onBack={() => navigate('home')}
            onNavigate={navigate}
          />
        );

      case 'blog-article':
        return (
          <BlogArticlePage
            onBack={() => navigate('blog')}
            onNavigate={navigate}
            slug={pageParams.slug}
            fallbackId={pageParams.fallbackId}
          />
        );
      
      case 'product':
        // For now, redirect to catalog with the product selected
        return (
          <PharmacyCatalogPage
            onNavigate={navigate}
            initialSearch={pageParams.id || ''}
            cart={cart}
            onAddToCart={addToCart}
          />
        );
      
      default:
        return (
          <PharmacyHomePage
            onNavigate={navigate}
            cartCount={cartCount}
          />
        );
    }
  };

  return (
    <div className="pharmacy-app">
      {renderPage()}
      {/* Chatbot visible on all pages */}
      <PharmacyChatbot onNavigateToCatalog={() => navigate('catalog')} />
    </div>
  );
};

export default PharmacyApp;
